import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getStore, getStoreSections, getActiveSections, addStoreSection, deleteStoreSection,
  getStoreSectionSkus, getSkus, addStoreSectionSku, updateStoreSectionSku, deleteStoreSectionSku
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowLeft, Plus, Trash2, Package, Layers, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function StoreConfigPage() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  
  const [store, setStore] = useState(null);
  const [storeSections, setStoreSections] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);
  const [allSkus, setAllSkus] = useState([]);
  const [sectionSkus, setSectionSkus] = useState({});
  const [loading, setLoading] = useState(true);
  
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [addSkuOpen, setAddSkuOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [newSectionId, setNewSectionId] = useState('');
  
  const [skuForm, setSkuForm] = useState({
    sku_id: '',
    min_stock: 5,
    wizard_order: 0,
    is_critical: false,
    facing: 1
  });

  useEffect(() => {
    loadData();
  }, [storeId]);

  const loadData = async () => {
    try {
      const [storeRes, sectionsRes, availableRes, skusRes] = await Promise.all([
        getStore(storeId),
        getStoreSections(storeId),
        getActiveSections(),
        getSkus({ active: true })
      ]);
      
      setStore(storeRes.data);
      setStoreSections(sectionsRes.data);
      setAvailableSections(availableRes.data);
      setAllSkus(skusRes.data);
      
      // Load SKUs for each section
      const skusBySection = {};
      for (const ss of sectionsRes.data) {
        const skusRes = await getStoreSectionSkus(storeId, ss.section_id);
        skusBySection[ss.section_id] = skusRes.data;
      }
      setSectionSkus(skusBySection);
      
    } catch (error) {
      toast.error('Error cargando datos');
      navigate('/stores');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSection = async () => {
    if (!newSectionId) return;
    try {
      await addStoreSection(storeId, { store_id: storeId, section_id: newSectionId, order: storeSections.length });
      toast.success('Sección agregada');
      setAddSectionOpen(false);
      setNewSectionId('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error agregando sección');
    }
  };

  const handleRemoveSection = async (storeSectionId) => {
    if (!window.confirm('¿Remover esta sección de la tienda?')) return;
    try {
      await deleteStoreSection(storeSectionId);
      toast.success('Sección removida');
      loadData();
    } catch (error) {
      toast.error('Error removiendo sección');
    }
  };

  const handleAddSku = async () => {
    if (!skuForm.sku_id || !selectedSection) return;
    try {
      await addStoreSectionSku({
        store_id: storeId,
        section_id: selectedSection,
        ...skuForm,
        min_stock: parseInt(skuForm.min_stock),
        wizard_order: parseInt(skuForm.wizard_order),
        facing: parseInt(skuForm.facing)
      });
      toast.success('SKU agregado a la sección');
      setAddSkuOpen(false);
      resetSkuForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error agregando SKU');
    }
  };

  const handleUpdateSku = async (skuConfigId, data) => {
    try {
      await updateStoreSectionSku(skuConfigId, data);
      toast.success('SKU actualizado');
      loadData();
    } catch (error) {
      toast.error('Error actualizando SKU');
    }
  };

  const handleRemoveSku = async (skuConfigId) => {
    try {
      await deleteStoreSectionSku(skuConfigId);
      toast.success('SKU desactivado de esta sección');
      loadData();
    } catch (error) {
      toast.error('Error removiendo SKU');
    }
  };

  const resetSkuForm = () => {
    setSkuForm({ sku_id: '', min_stock: 5, wizard_order: 0, is_critical: false, facing: 1 });
    setSelectedSection(null);
  };

  const openAddSkuDialog = (sectionId) => {
    setSelectedSection(sectionId);
    setSkuForm({ ...skuForm, wizard_order: (sectionSkus[sectionId]?.length || 0) });
    setAddSkuOpen(true);
  };

  // Filter out already assigned sections
  const unassignedSections = availableSections.filter(
    s => !storeSections.some(ss => ss.section_id === s.id)
  );

  // Filter out already assigned SKUs in selected section
  const getAvailableSkus = () => {
    if (!selectedSection) return allSkus;
    const assignedIds = new Set((sectionSkus[selectedSection] || []).map(s => s.sku_id));
    return allSkus.filter(s => !assignedIds.has(s.id));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="store-config-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/stores')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">
            Configurar Tienda
          </h1>
          <p className="text-slate-500">
            {store?.chain_name} - {store?.name}
          </p>
        </div>
      </div>

      {/* Sections Management */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-['Chivo']">
            <Layers className="w-5 h-5 inline mr-2" />
            Secciones de la Tienda
          </CardTitle>
          <Dialog open={addSectionOpen} onOpenChange={setAddSectionOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-section-to-store-btn">
                <Plus className="w-4 h-4 mr-1" /> Agregar Sección
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Agregar Sección a la Tienda</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Sección</Label>
                  <Select value={newSectionId} onValueChange={setNewSectionId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar sección" />
                    </SelectTrigger>
                    <SelectContent>
                      {unassignedSections.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name} ({s.code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {unassignedSections.length === 0 && (
                    <p className="text-sm text-slate-500">Todas las secciones ya están asignadas</p>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setAddSectionOpen(false)}>Cancelar</Button>
                  <Button onClick={handleAddSection} disabled={!newSectionId} className="bg-blue-600 hover:bg-blue-700">
                    Agregar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {storeSections.length === 0 ? (
            <p className="text-center py-8 text-slate-500">
              No hay secciones configuradas. Agrega secciones para asignar SKUs.
            </p>
          ) : (
            <Accordion type="multiple" className="space-y-2">
              {storeSections.map((ss) => (
                <AccordionItem key={ss.id} value={ss.section_id} className="border rounded-lg">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{ss.section_code}</Badge>
                        <span className="font-medium">{ss.section_name}</span>
                        <Badge variant="secondary">{ss.sku_count} SKUs</Badge>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="flex justify-between items-center mb-4">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openAddSkuDialog(ss.section_id)}
                        data-testid={`add-sku-to-${ss.section_id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Agregar SKU
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleRemoveSection(ss.id)}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Remover Sección
                      </Button>
                    </div>
                    
                    {sectionSkus[ss.section_id]?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>Orden</TableHead>
                            <TableHead>SKU</TableHead>
                            <TableHead>Marca</TableHead>
                            <TableHead className="text-right">Stock Mínimo</TableHead>
                            <TableHead>Crítico</TableHead>
                            <TableHead>Activo</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sectionSkus[ss.section_id].map((sku) => (
                            <TableRow key={sku.id}>
                              <TableCell className="font-mono">{sku.wizard_order}</TableCell>
                              <TableCell className="font-medium">{sku.sku_name}</TableCell>
                              <TableCell className="text-slate-500">{sku.sku_brand}</TableCell>
                              <TableCell className="text-right">
                                <Input
                                  type="number"
                                  className="w-20 h-8 text-right"
                                  value={sku.min_stock}
                                  onChange={(e) => handleUpdateSku(sku.id, { min_stock: parseInt(e.target.value) || 0 })}
                                  data-testid={`min-stock-${sku.id}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Switch
                                  checked={sku.is_critical}
                                  onCheckedChange={(v) => handleUpdateSku(sku.id, { is_critical: v })}
                                />
                              </TableCell>
                              <TableCell>
                                <Switch
                                  checked={sku.active}
                                  onCheckedChange={(v) => handleUpdateSku(sku.id, { active: v })}
                                />
                              </TableCell>
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => handleRemoveSku(sku.id)}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-4 text-slate-500 text-sm">
                        No hay SKUs en esta sección
                      </p>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Add SKU Dialog */}
      <Dialog open={addSkuOpen} onOpenChange={(open) => { setAddSkuOpen(open); if (!open) resetSkuForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar SKU a la Sección</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>SKU</Label>
              <Select value={skuForm.sku_id} onValueChange={(v) => setSkuForm({...skuForm, sku_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar SKU" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableSkus().map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} - {s.brand}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Stock Mínimo</Label>
                <Input
                  type="number"
                  value={skuForm.min_stock}
                  onChange={(e) => setSkuForm({...skuForm, min_stock: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Orden en Wizard</Label>
                <Input
                  type="number"
                  value={skuForm.wizard_order}
                  onChange={(e) => setSkuForm({...skuForm, wizard_order: e.target.value})}
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={skuForm.is_critical}
                  onCheckedChange={(v) => setSkuForm({...skuForm, is_critical: v})}
                />
                <Label>Es Crítico</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddSkuOpen(false)}>Cancelar</Button>
              <Button onClick={handleAddSku} disabled={!skuForm.sku_id} className="bg-blue-600 hover:bg-blue-700">
                Agregar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
